import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  getInboxThreadApi,
  initInboxThreadApi,
  deleteMessageApi,
  getServerBaseUrl,
} from '../../api';
import MessageBubble from './MessageBubble';
import ConfirmModal from '../common/ConfirmModal';

function formatKoreanTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? '오후' : '오전';
  hours = hours % 12;
  hours = hours ? hours : 12;
  return `${ampm} ${hours}:${minutes}`;
}

function formatKoreanDateDivider(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    }).format(d);
  } catch (e) {
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();
    return `${year}년 ${month}월 ${day}일`;
  }
}

function getDateKey(dateStr) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const ChatMessagePanel = React.memo(function ChatMessagePanel({
  roomId,
  currentUserId,
  currentUserRole,
  viewportVersion = 0,
  scrollContainerRef: externalScrollRef,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const [threadInfo, setThreadInfo] = useState(null);
  const messagesEndRef = useRef(null);
  const fallbackScrollRef = useRef(null);
  const scrollContainerRef = externalScrollRef || fallbackScrollRef;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);

  useEffect(() => {
    if (!roomId) return;

    // ✅ 공통 서버 베이스 URL 사용 (예: http(s)://도메인:4000)
    const SOCKET_URL = getServerBaseUrl();

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });

    socket.emit('join_room', `thread_${roomId}`);

    socket.on('new_message', (newMessage) => {
      if (String(newMessage.thread_id) === String(roomId)) {
        setMessages((prev) => {
          if (prev.find((m) => m.message_id === newMessage.message_id)) return prev;
          return [...prev, newMessage];
        });
      }
    });

    socket.on('message_deleted', (data) => {
      if (String(data.thread_id) === String(roomId)) {
        setMessages((prev) => prev.filter((m) => m.message_id !== data.message_id));
      }
    });

    socket.on('thread_cleared', (data) => {
      if (String(data?.thread_id) === String(roomId) && currentUserRole === 'ADMIN') {
        setMessages([]);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [roomId, currentUserRole]);

  useEffect(() => {
    if (loading || messages.length === 0) return;
    const container = scrollContainerRef.current;
    const endEl = messagesEndRef.current;
    if (!container || !endEl) return;

    const last = messages[messages.length - 1];
    const lastIsMe =
      currentUserId !== null && Number(last?.sender_id) === Number(currentUserId);
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 40;

    if (lastIsMe) {
      container.scrollTop = container.scrollHeight;
      endEl.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (isNearBottom) {
      endEl.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, currentUserId, scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const endEl = messagesEndRef.current;
    if (!endEl) return;
    container.scrollTop = container.scrollHeight;
    endEl.scrollIntoView({ behavior: 'auto' });
  }, [viewportVersion, scrollContainerRef]);

  useEffect(() => {
    let mounted = true;

    const fetchThread = async () => {
      try {
        setLoading(true);
        setError('');

        const res = await getInboxThreadApi(roomId);
        const data = res?.data;

        if (!mounted) return;
        setMessages(Array.isArray(data?.messages) ? data.messages : []);
        setThreadInfo(data?.thread || null);
      } catch (e) {
        if (!mounted) return;
        const status = e?.status;

        if (status === 404 && currentUserRole === 'MEMBER') {
          try {
            const initRes = await initInboxThreadApi();
            const newThreadId = initRes?.data?.thread?.thread_id;
            if (newThreadId) {
              navigate(`/inbox/${newThreadId}`, { replace: true });
              return;
            }
          } catch (initErr) {
            setError(initErr?.message || '쪽지함을 초기화하지 못했습니다.');
            return;
          }
        }

        setError(e?.message || '쪽지 내용을 불러오지 못했습니다.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (roomId) fetchThread();
    else {
      setLoading(false);
      setError('잘못된 대화방입니다.');
    }

    return () => {
      mounted = false;
    };
  }, [roomId, currentUserRole, navigate]);

  const uiMessages = useMemo(() => {
    const mapped = messages.map((m) => {
      const isMe = currentUserId ? Number(m.sender_id) === Number(currentUserId) : false;
      let senderName = '';
      if (!isMe) {
        if (currentUserRole === 'MEMBER') senderName = '관리자';
        else senderName = threadInfo?.user_name || '회원';
      }
      const time = formatKoreanTime(m.created_at);
      const dateKey = getDateKey(m.created_at);
      return {
        id: m.message_id,
        content: m.content,
        time,
        createdAt: m.created_at,
        dateKey,
        dateLabel: formatKoreanDateDivider(m.created_at),
        isMe,
        senderName,
      };
    });

    for (let i = 0; i < mapped.length; i += 1) {
      const cur = mapped[i];
      const prev = mapped[i - 1];
      const next = mapped[i + 1];

      const isLastOfSameTimeGroup =
        !next || next.dateKey !== cur.dateKey || next.time !== cur.time || next.isMe !== cur.isMe;

      cur.showTime = isLastOfSameTimeGroup;

      const isFirstOfSameTimeGroup =
        !prev ||
        prev.dateKey !== cur.dateKey ||
        prev.time !== cur.time ||
        prev.isMe !== cur.isMe ||
        prev.senderName !== cur.senderName;

      cur.showSenderName = !cur.isMe && !!cur.senderName && isFirstOfSameTimeGroup;
      cur.showTail = isFirstOfSameTimeGroup;
    }

    return mapped;
  }, [messages, currentUserId, currentUserRole, threadInfo]);

  const handleDeleteClick = (messageId) => {
    setSelectedMessageId(messageId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMessageId) {
      setDeleteModalOpen(false);
      return;
    }

    try {
      await deleteMessageApi(roomId, selectedMessageId);
      setMessages((prev) => prev.filter((m) => m.message_id !== selectedMessageId));
      setDeleteModalOpen(false);
      setSelectedMessageId(null);
    } catch (error) {
      console.error('Failed to delete message:', error);
      if (error?.status === 401 || error?.suppressAlert) return;
      alert('메시지 삭제에 실패했습니다.');
      setDeleteModalOpen(false);
      setSelectedMessageId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedMessageId(null);
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
          <CircularProgress size={18} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Skeleton variant="rounded" height={56} width="70%" />
          <Skeleton variant="rounded" height={56} width="55%" sx={{ alignSelf: 'flex-end' }} />
          <Skeleton variant="rounded" height={56} width="68%" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2">
        {error}
      </Typography>
    );
  }

  return (
    <>
      {uiMessages.map((m, idx) => {
        const prev = uiMessages[idx - 1];
        const showDateDivider = idx === 0 || prev?.dateKey !== m.dateKey;
        return (
          <React.Fragment key={m.id}>
            {showDateDivider ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    bgcolor: 'grey.200',
                    borderRadius: 999,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {m.dateLabel}
                  </Typography>
                </Box>
              </Box>
            ) : null}
            <MessageBubble
              message={m}
              isMe={m.isMe}
              senderName={m.senderName}
              currentUserRole={currentUserRole}
              onDeleteClick={handleDeleteClick}
            />
          </React.Fragment>
        );
      })}
      {uiMessages.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          메시지가 없습니다.
        </Typography>
      ) : null}
      <div ref={messagesEndRef} />
      <ConfirmModal
        open={deleteModalOpen}
        message="이 메시지를 삭제하시겠습니까?"
        confirmText="삭제"
        cancelText="취소"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
});

export default ChatMessagePanel;
